import { useContext, useMemo, useState, useCallback, useRef } from "react";
import { BotReplyLayout } from "./bot.reply.layout.component";
import { EonUiButton } from "@eon-ui/eon-ui-components-react";
import { useNexthinkContext } from "./nexthink.context";
import { reportAndRecommendedActions } from "./smart.actions.benchmark";
import { processRemoteAction } from "@me8eon/ai.tools.nextthink";
import { AiAssistantContext } from "../../../../context/AiAssistantContext";
import LoaderComponent from "../../../loader-component/LoaderComponent";
import { randomUniqueId } from "@me8eon/string_utils";
import { CheckboxWithDescription } from "@me8eon/checkbox"
import SystemCheckMessageList from "./system.check.message.list";
import { useJwtToken } from "@me8eon/react_authentication";

export const NexthinkReportAndActions: React.FC = () => {
    const { loading, healthCheckInfo, setActionStatus, actionFreeze, setActionFreeze, setManualRecommendedActions, selectedDevice, localT } = useNexthinkContext();
  const { addAssistantTask } = useContext(AiAssistantContext);
  const [selectedActions, setSelectedActions] = useState<string[]>([]);
  const [showMoreInfo, setShowMoreInfo] = useState(false);
  const alreadyRan = useRef(false);
  const jwtTokenFn = useJwtToken();

  // derive report & actions
  const { report = [], recommendedActions = [], recommendedFixes = [] } = useMemo(() => {
    return reportAndRecommendedActions(healthCheckInfo) || {};
  }, [healthCheckInfo]);

  useMemo(() => {
    if (recommendedFixes.length > 0 && recommendedActions.length > 0) {
      setManualRecommendedActions(recommendedFixes);
    }
  }, [recommendedFixes]);

  const { id: deviceId, name: deviceName } = selectedDevice || {};

  // handlers
  const toggleActionSelection = useCallback((action: string) => {
    setSelectedActions((prev) =>
      prev.includes(action) ? prev.filter((a) => a !== action) : [...prev, action]
    );
  }, []);

  const initiateActions = useCallback(async () => {
    if (!deviceId) return;
    setActionStatus({ status: "progress" });
    try {
      const results = await Promise.allSettled(
        selectedActions.map(async (action) => {
          const result = await processRemoteAction(action, deviceId, jwtTokenFn);
          if (result.requestId) {
            addAssistantTask({ requestId: result.requestId, task: action, status: "progress", timestamp: new Date().getTime() });
            return { action, id: result.requestId };
          } else {
            const fakeRequestId = randomUniqueId(action);
            addAssistantTask({ requestId: fakeRequestId, task: action, status: "failure", timestamp: new Date().getTime() });
            return { action, id: fakeRequestId };
          }
        })
      );
      setActionStatus({ status: "progress", response: results });
    } catch (err) {
      console.error(err);
      setActionStatus({ status: "failed" });
    }
  }, [deviceId, selectedActions, addAssistantTask, setActionStatus]);

  const initiateSingleAction = useCallback(
    async (action: string) => {
      if (!deviceId) return;
      setActionStatus({ status: "progress" });
      const result = await processRemoteAction(action, deviceId, jwtTokenFn);
      if (result.requestId) {
        addAssistantTask({ requestId: result.requestId, task: action, status: "progress", timestamp: new Date().getTime() });
        setActionStatus({ status: "progress", response: [{ value: { id: result.requestId, action } }] });
      } else {
        const fakeRequestId = randomUniqueId(action);
        addAssistantTask({ requestId: fakeRequestId, task: action, status: "failure", timestamp: new Date().getTime() });
        setActionStatus({ status: "failed" });
      }
    },
    [deviceId, addAssistantTask, setActionStatus]
  );

  const handleInitiate = () => {
    setActionFreeze((prev) => ({ ...prev, triggerActions: true }));
    if (recommendedActions.length > 1) {
      initiateActions();
    } else {
      initiateSingleAction(recommendedActions[0].id);
    }
  };

  const handleEffectEnd = useCallback(() => {
    alreadyRan.current = true;
    setShowMoreInfo(true);
  }, []);

  // render
  if (loading) {
    return (
      <BotReplyLayout>
        <div className="report-loading">
          <LoaderComponent isLoading={loading} isInline size="small" />
          <span>
                        {localT("chatBot.nexthinkIntegration.smartSolution.healthCheckApiRunning")} {deviceName}
          </span>
        </div>
      </BotReplyLayout>
    );
  }

  if (!healthCheckInfo) return null;

  return (
      <>
          <BotReplyLayout>
              <div className="report-and-actions">
                  <div className="report">
                      <div className="report__heading">
                          {report.length === 0 && <SystemCheckMessageList effectEnd={handleEffectEnd} alreadyRan={alreadyRan.current} />}
                          {report.length > 0 && (
                              <div>
                                  {localT(
                                      "chatBot.nexthinkIntegration.smartSolution.healthCheckApiSuccess"
                                  )}
                              </div>
                          )}
                      </div>
                      {report.length > 0 && (
                          <div className="report__parameters">
                              <ul>
                                  {report.map((msg, index) => (
                                      <li key={index}>{msg}</li>
                                  ))}
                              </ul>
                          </div>
                      )}
                      {recommendedFixes.length > 0 &&
                          recommendedActions.length > 0 && (
                              <div className="report__footer">
                                  <b>
                                      {localT(
                                          "chatBot.nexthinkIntegration.smartSolution.manualActionDisclaimer"
                                      )}
                                  </b>
                              </div>
                          )}
                  </div>

                  <div className="actions">
                      {recommendedActions.length > 0 && (
                          <>
                              <div className="actions__statements">
                                  <span className="actions__title">
                                      {localT(
                                          "chatBot.nexthinkIntegration.smartSolution.recommendedActionHeading"
                                      )}
                                  </span>
                              </div>
                              <div className="actions__items">
                                  {recommendedActions.length === 1 && (
                                      <div className="actions__item-single">
                                          <span>
                                              {localT(recommendedActions[0].name)}
                                          </span>
                                          <span>
                                              {localT(
                                                  recommendedActions[0]?.info ||
                                                      ""
                                              )}
                                          </span>
                                      </div>
                                  )}
                                  {recommendedActions.length > 1 &&
                                      recommendedActions.map(
                                          (action, index) => (
                                              <div
                                                  className="actions__item"
                                                  key={index}
                                              >
                                                  <CheckboxWithDescription
                                                      label={localT(action.name)}
                                                      value={selectedActions.includes(
                                                          action.id
                                                      )}
                                                      description={localT(
                                                          action?.info || ""
                                                      )}
                                                      onValueChanged={() =>
                                                          toggleActionSelection(
                                                              action.id
                                                          )
                                                      }
                                                      disabled={
                                                          actionFreeze.triggerActions
                                                      }
                                                  />
                                              </div>
                                          )
                                      )}
                              </div>
                              <div className="actions__footer">
                                  <EonUiButton
                                      scheme="red500"
                                      text={
                                          recommendedActions.length > 1
                                              ? localT(
                                                  "chatBot.nexthinkIntegration.smartSolution.initiateActionsButton"
                                              )
                                              : localT(recommendedActions[0].name)
                                      }
                                      rank="primary"
                                      size="small"
                                      disabled={
                                          actionFreeze.triggerActions ||
                                          (recommendedActions.length > 1 &&
                                              !selectedActions.length)
                                      }
                                      onClick={handleInitiate}
                                  />
                              </div>
                          </>
                      )}
                      {recommendedFixes.length > 0 &&
                          recommendedActions.length < 1 && (
                              <div className="actions__statements">
                                  <span className="actions__title">
                                      {localT(
                                          "chatBot.nexthinkIntegration.smartSolution.recommendedFixesHeading"
                                      )}
                                  </span>
                                  <ul>
                                      {recommendedFixes.map((fix, index) => (
                                          <li key={index}>{localT(fix)}</li>
                                      ))}
                                  </ul>
                              </div>
                          )}
                  </div>
              </div>
          </BotReplyLayout>
          {report.length < 1 && showMoreInfo && (
              <BotReplyLayout>
                  <span>
                      {localT(
                          "chatBot.nexthinkIntegration.smartSolution.askForElaboration"
                      )}
                  </span>
              </BotReplyLayout>
          )}
      </>
  );
};
